import React from "react";
import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";
import Comp18PDF from "./Comp18PDF";

const sectionTitleSx = { mt: 4, mb: 1, fontWeight: 700 };
const labelSx = { width: "180px", fontWeight: 700, flexShrink: 0 };
const valueSx = {
  flex: 1,
  borderBottom: "1px solid #999",
  minHeight: "20px",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};
const rowSx = { display: "flex", gap: 12, mb: 1.2, alignItems: "flex-start" };
const gridSx = {
  display: "grid",
  gridTemplateColumns: "180px 1fr 180px 1fr",
  gap: "6px 12px",
  mb: 2,
};

const valueOrEmpty = (value) => value ?? "";
const boolLabel = (value, yes = "あり", no = "なし") => {
  if (value === true) return yes;
  if (value === false) return no;
  return "";
};
const sameAsLabel = (value) => {
  if (value === "same") return "同じ";
  if (value === "different") return "異なる";
  return "";
};
const vehicleTypeLabel = (kind) => {
  const labels = {
    mini: "軽車両（2tクラス以下）",
    norm: "中型車両（4tクラス以下）",
    big: "大型車両（10tクラス以下）",
    trail: "トレーラー",
    crain: "クレーン",
    fork: "フォークリフト",
    other: "その他",
  };
  return labels[kind] || kind || "";
};

const LabeledRow = ({ label, value }) => (
  <Box sx={rowSx}>
    <Typography sx={labelSx}>{label}</Typography>
    <Box sx={valueSx}>{valueOrEmpty(value)}</Box>
  </Box>
);

const ContactGrid = ({ rows }) => (
  <Box sx={gridSx}>
    {rows.map(([label, value]) => (
      <React.Fragment key={label}>
        <Box sx={{ fontWeight: 700 }}>{label}</Box>
        <Box sx={valueSx}>{valueOrEmpty(value)}</Box>
      </React.Fragment>
    ))}
  </Box>
);

const CompanySection = ({ title, enabled, data }) => (
  <Box sx={{ mt: 2 }}>
    <FormControlLabel control={<Checkbox checked={!!enabled} readOnly />} label={title} />
    {!!enabled && (
      <ContactGrid
        rows={[
          ["会社名", data.company_name],
          ["会社電話番号", data.company_tel],
          ["携帯電話番号", data.phone_number],
          ["郵便番号", data.company_zip],
          ["住所", data.company_address],
          ["FAX番号", data.company_fax],
          ["担当者名", data.name],
        ]}
      />
    )}
  </Box>
);

const VehicleApplicantList = ({ title, items }) => (
  <Box sx={{ mt: 3 }}>
    <Typography sx={sectionTitleSx}>{title}</Typography>
    {!items?.length && <Typography>入力なし</Typography>}
    {items?.map((item, index) => (
      <Box key={`${item.car_kind}-${index}`} sx={{ mb: 2, pl: 1 }}>
        <Typography sx={{ mb: 1, fontWeight: 700 }}>
          {`車両 ${index + 1} : ${vehicleTypeLabel(item.car_kind)}`}
        </Typography>
        <LabeledRow label="会社名" value={item.cert_comp} />
        <LabeledRow label="担当者名" value={item.cert_user} />
        <LabeledRow label="携帯電話番号" value={item.cert_phone} />
      </Box>
    ))}
  </Box>
);

const ExhibitDetailList = ({ details }) => (
  <Box sx={{ mt: 3 }}>
    <Typography sx={sectionTitleSx}>搬入物の詳細</Typography>
    {!details?.length && <Typography>入力なし</Typography>}
    {details?.map((detail, index) => (
      <Box key={index} sx={{ mb: 2, pl: 1 }}>
        <Typography sx={{ mb: 1, fontWeight: 700 }}>{`搬入物 ${index + 1}`}</Typography>
        <ContactGrid
          rows={[
            ["搬入物名", detail.exhibition],
            ["個数", detail.number],
            ["重量(kg)", detail.weight],
            ["長さ(mm)", detail.length],
            ["幅(mm)", detail.width],
            ["高さ(mm)", detail.height],
          ]}
        />
      </Box>
    ))}
  </Box>
);

const FormRequirePDF = ({ reqData, User0 }) => {
  if (!reqData) return null;

  const r1 = reqData.r1 ?? {};
  const r2 = reqData.r2 ?? {};
  const certDetails = Array.isArray(r2.cert_details) ? r2.cert_details : [];
  const indoorDetails = Array.isArray(r2.indoor_details) ? r2.indoor_details : [];
  const details = Array.isArray(r2.details) ? r2.details : [];

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        出展申込 基本情報
      </Typography>

      <LabeledRow label="小間番号" value={reqData.booth_number} />
      <LabeledRow label="会社名" value={reqData.company_name} />
      <LabeledRow label="部署" value={reqData.department} />
      <LabeledRow label="担当者名" value={reqData.user_name} />
      <LabeledRow label="郵便番号" value={reqData.zip_code} />
      <LabeledRow label="住所" value={reqData.address} />
      <LabeledRow label="電話番号" value={reqData.phone_number} />
      <LabeledRow label="メールアドレス" value={reqData.email} />

      <Typography variant="h6" sx={{ mt: 5, mb: 2 }}>
        出展者情報
      </Typography>

      <Typography sx={sectionTitleSx}>責任者情報</Typography>
      <ContactGrid
        rows={[
          ["担当者名", r1.manager_name],
          ["部署", r1.manager_department],
          ["会社電話番号", r1.manager_phone],
          ["携帯電話番号", r1.manager_tel],
        ]}
      />

      <Typography sx={sectionTitleSx}>担当者情報</Typography>
      <LabeledRow label="出展者情報と同じ" value={sameAsLabel(r1.same_ex_1)} />
      <ContactGrid
        rows={[
          ["担当者名", r1.user_1_name],
          ["部署", r1.user_1_department],
          ["会社電話番号", r1.user_1_phone],
          ["携帯電話番号", r1.user_1_tel],
        ]}
      />

      <Typography sx={sectionTitleSx}>誓約・ブース装飾</Typography>
      <FormControlLabel control={<Checkbox checked={!!r1.pledge} readOnly />} label="出展の手引きを確認し、内容に同意する" />
      {User0 && (
        <>
          <FormControlLabel control={<Checkbox checked={!!r1.panel_pledge} readOnly />} label="システムパネルの追加変更を申請する" />
          {!!r1.panel_pledge && (
            <>
              <LabeledRow label="追加変更内容" value={r1.panel_question} />
              <LabeledRow label="表示プラン" value={r1.panel_plan} />
            </>
          )}
        </>
      )}

      <Typography sx={sectionTitleSx}>委託会社情報</Typography>
      <CompanySection
        title="装飾委託会社あり"
        enabled={r1.has_package_company}
        data={{
          company_name: r1.package_company_name,
          company_tel: r1.package_company_tel,
          phone_number: r1.package_phone_number,
          company_zip: r1.package_company_zip,
          company_address: r1.package_company_address,
          company_fax: r1.package_company_fax,
          name: r1.package_name,
        }}
      />
      <CompanySection
        title="電気工事委託会社あり"
        enabled={r1.has_elec_company}
        data={{
          company_name: r1.elec_company_name,
          company_tel: r1.elec_company_tel,
          phone_number: r1.elec_phone_number,
          company_zip: r1.elec_company_zip,
          company_address: r1.elec_company_address,
          company_fax: r1.elec_company_fax,
          name: r1.elec_name,
        }}
      />
      <CompanySection
        title="運送委託会社あり"
        enabled={r1.has_cargo_company}
        data={{
          company_name: r1.cargo_company_name,
          company_tel: r1.cargo_company_tel,
          phone_number: r1.cargo_phone_number,
          company_zip: r1.cargo_company_zip,
          company_address: r1.cargo_company_address,
          company_fax: r1.cargo_company_fax,
          name: r1.cargo_name,
        }}
      />

      <Typography sx={sectionTitleSx}>備考</Typography>
      <Box sx={valueSx}>{valueOrEmpty(r1.alfa_text)}</Box>

      <Typography variant="h6" sx={{ mt: 5, mb: 2 }}>
        搬入・搬出申請
      </Typography>

      <Typography sx={sectionTitleSx}>担当者情報</Typography>
      <LabeledRow label="出展者情報と同じ" value={sameAsLabel(r2.same_ex_2)} />
      <ContactGrid
        rows={[
          ["担当者名", r2.user_2_name],
          ["部署", r2.user_2_department],
          ["会社電話番号", r2.user_2_phone],
          ["携帯電話番号", r2.user_2_tel],
        ]}
      />

      <Typography sx={sectionTitleSx}>搬入方法・搬入日</Typography>
      <LabeledRow label="搬入日" value={r2.reserve_date} />

      <Typography sx={sectionTitleSx}>搬入出車両申請</Typography>
      <LabeledRow label="搬入出車両申請" value={boolLabel(r2.car_certification, "申請する", "申請しない")} />
      <VehicleApplicantList title="搬入出車両 申請者情報" items={certDetails} />

      <Typography sx={sectionTitleSx}>館内への車両乗り入れ</Typography>
      <LabeledRow label="館内への車両乗り入れ" value={boolLabel(r2.indoor_car, "あり", "なし")} />
      <VehicleApplicantList title="館内乗り入れ車両 申請者情報" items={indoorDetails} />

      <ExhibitDetailList details={details} />

      <Typography sx={sectionTitleSx}>備考</Typography>
      <Box sx={valueSx}>{valueOrEmpty(r2.alfa_text)}</Box>

      {reqData.r_catch && (
        <Box sx={{ mt: 5 }}>
          <Comp18PDF
            formData={reqData.r_catch}
            boothNumber={reqData.booth_number}
            companyName={reqData.company_name}
          />
        </Box>
      )}
    </Box>
  );
};

export default FormRequirePDF;
